import { useEffect, useRef, useCallback, useState, type ReactNode } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, RotateCcw, ChevronUp } from 'lucide-react';
import { useAgentsList } from './hooks/useAgentsList';
import { useConversation } from './hooks/useConversation';
import { MessageBubble } from './components/MessageBubble';
import { MessageInput } from './components/MessageInput';
import { TypingIndicator } from './components/TypingIndicator';
import { messengerService } from './services/messengerService';
import { cn, getApiBase } from '../../lib/utils';
import { PushNotificationManager } from '../../components/PushNotificationManager';
import { useGuidedFlow } from '../../hooks/useGuidedFlow';
import FlowDeliverablesBadge from '../../components/flow/FlowDeliverablesBadge';
import FlowDeliverablesDrawer from '../../components/flow/FlowDeliverablesDrawer';
import { getFlowSessionStatus, loadFlowSessionToken } from '../../lib/flowApi';

function AgentHeader({
  name,
  iconUrl,
  onBack,
  onReset,
  extraActions,
}: {
  name: string;
  iconUrl?: string | null;
  onBack: () => void;
  onReset: () => void;
  extraActions?: ReactNode;
}) {
  const initial = name.charAt(0).toUpperCase();
  const absoluteUrl = iconUrl
    ? iconUrl.startsWith('http') ? iconUrl : `${getApiBase()}${iconUrl}`
    : null;

  return (
    <div className="bg-zinc-900 border-b border-zinc-800 px-4 py-3 flex items-center gap-3 shrink-0">
      <button
        onClick={onBack}
        className="text-zinc-400 hover:text-white transition-colors p-1 -ml-1 rounded-lg hover:bg-zinc-800"
        aria-label="Retour"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      {absoluteUrl ? (
        <img
          src={absoluteUrl}
          alt={name}
          className="h-9 w-9 rounded-full object-cover bg-zinc-800 shrink-0"
        />
      ) : (
        <div className="h-9 w-9 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
          <span className="text-emerald-400 font-semibold">{initial}</span>
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="font-semibold text-white text-sm truncate">{name}</p>
        <p className="text-[11px] text-emerald-400">En ligne</p>
      </div>

      {extraActions}

      <button
        onClick={onReset}
        title="Nouvelle conversation"
        className="text-zinc-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-zinc-800"
        aria-label="Nouvelle conversation"
      >
        <RotateCcw className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function ChatPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const parsedId = Number(agentId);
  const focusConversationId = (() => {
    const raw = searchParams.get('conversation');
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const { agents, refresh: refreshAgents } = useAgentsList();
  const agent = agents.find(a => a.id === parsedId);

  const { messages, loading, loadingOlder, hasMore, sending, statusMessage, conversationId, sendMessage, loadOlder, reset } =
    useConversation(parsedId, focusConversationId);

  const {
    flowMeta,
    flowSessionToken,
    restoreTokenForConversation,
    hydrateFromStatus,
    resetFlow,
    showDeliverablesButton,
  } = useGuidedFlow();

  const [deliverablesOpen, setDeliverablesOpen] = useState(false);
  const [flowTemplateId, setFlowTemplateId] = useState<number | null>(null);
  const isGuidedAgent = agent?.agent_mode === 'guided';

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);
  const prevScrollHeightRef = useRef(0);
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  useEffect(() => {
    if (!conversationId || !isGuidedAgent) {
      resetFlow();
      setFlowTemplateId(null);
      return;
    }
    let cancelled = false;
    restoreTokenForConversation(conversationId);
    const token = loadFlowSessionToken(conversationId);
    getFlowSessionStatus(conversationId, token)
      .then((status) => {
        if (cancelled) return;
        if (!status?.guided_flow) {
          resetFlow();
          setFlowTemplateId(null);
          return;
        }
        hydrateFromStatus({
          guided_flow: status.guided_flow,
          flow_session_token: status.flow_session_token,
          conversation_id: conversationId,
        });
        setFlowTemplateId(status.flow_template_id ?? agent?.flow_template_id ?? null);
      })
      .catch(() => {
        if (!cancelled) {
          resetFlow();
          setFlowTemplateId(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [conversationId, isGuidedAgent, agent?.flow_template_id, hydrateFromStatus, resetFlow, restoreTokenForConversation]);

  // Scroll to bottom on initial load + new sent messages
  useEffect(() => {
    if (loading) return;
    if (isInitialLoad.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'instant' as ScrollBehavior });
      isInitialLoad.current = false;
      return;
    }
    // Scroll smooth seulement si on est déjà en bas
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < 200) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, sending, loading]);

  // Reset isInitialLoad quand on change d'agent
  useEffect(() => {
    isInitialLoad.current = true;
  }, [parsedId, focusConversationId]);

  // Stabilisation du scroll après loadOlder :
  // sauvegarder scrollHeight avant, restaurer après l'insertion des messages en haut
  useEffect(() => {
    if (loadingOlder) {
      prevScrollHeightRef.current = scrollContainerRef.current?.scrollHeight ?? 0;
    } else if (prevScrollHeightRef.current > 0) {
      const container = scrollContainerRef.current;
      if (container) {
        const newScrollHeight = container.scrollHeight;
        const delta = newScrollHeight - prevScrollHeightRef.current;
        container.scrollTop += delta;
      }
      prevScrollHeightRef.current = 0;
    }
  }, [loadingOlder]);

  // IntersectionObserver sur le sentinel haut → déclenche loadOlder
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingOlder && !loading) {
          loadOlder();
        }
      },
      { root: scrollContainerRef.current, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadingOlder, loading, loadOlder]);

  // Bouton "scroll to bottom" quand on est loin du bas
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    setShowScrollBottom(distanceFromBottom > 300);
  }, []);

  // Marquer les messages comme lus à l'ouverture
  useEffect(() => {
    if (!isNaN(parsedId)) messengerService.markRead(parsedId);
  }, [parsedId]);

  // Rafraîchir la liste des contacts après envoi
  const handleSend = useCallback((text: string) => {
    sendMessage(text);
    setTimeout(() => refreshAgents(), 3000);
  }, [sendMessage, refreshAgents]);

  const handleReset = useCallback(() => {
    reset();
    resetFlow();
    setDeliverablesOpen(false);
    if (focusConversationId) {
      navigate(`/messenger/${parsedId}`, { replace: true });
    }
  }, [reset, resetFlow, focusConversationId, navigate, parsedId]);

  if (isNaN(parsedId)) {
    navigate('/messenger');
    return null;
  }

  return (
    <div className="flex flex-col h-full relative">
      {/* Push Notification Manager - invisible */}
      <PushNotificationManager agentId={parsedId} />

      {/* Header */}
      <AgentHeader
        name={agent?.name ?? `Agent #${parsedId}`}
        iconUrl={agent?.icon_preview_url || agent?.icon_url}
        onBack={() => navigate('/messenger')}
        onReset={handleReset}
        extraActions={
          showDeliverablesButton && flowMeta ? (
            <FlowDeliverablesBadge
              done={flowMeta.deliverables_done.length}
              total={flowMeta.deliverable_total}
              onClick={() => setDeliverablesOpen(true)}
            />
          ) : null
        }
      />

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-2 scrollbar-thin scrollbar-thumb-zinc-800"
      >
        {/* Sentinel haut — déclenche loadOlder via IntersectionObserver */}
        <div ref={topSentinelRef} className="h-1" />

        {/* Indicateur chargement messages plus anciens */}
        {loadingOlder && (
          <div className="flex items-center justify-center py-3">
            <div className="w-4 h-4 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin" />
            <span className="ml-2 text-xs text-zinc-500">Chargement des messages précédents…</span>
          </div>
        )}

        {/* Indicateur "début de l'historique" */}
        {!hasMore && !loading && messages.length > 0 && (
          <div className="flex items-center gap-2 py-2">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-[11px] text-zinc-600 shrink-0">Début de la conversation</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>
        )}

        {/* Chargement initial */}
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {/* Empty state */}
        {!loading && messages.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className={cn(
              'h-16 w-16 rounded-full bg-emerald-500/20 border border-emerald-500/30',
              'flex items-center justify-center mb-4'
            )}>
              <span className="text-emerald-400 font-bold text-2xl">
                {(agent?.name ?? 'A').charAt(0).toUpperCase()}
              </span>
            </div>
            <p className="text-sm font-medium text-zinc-300">
              {agent?.name ?? `Agent #${parsedId}`}
            </p>
            <p className="text-xs text-zinc-500 mt-1 max-w-[200px]">
              {agent?.description || 'Envoyez un message pour démarrer la conversation'}
            </p>
          </div>
        )}

        {messages.map(msg => (
          <MessageBubble key={msg.id} message={msg} />
        ))}

        {sending && <TypingIndicator statusMessage={statusMessage} />}

        <div ref={bottomRef} />
      </div>

      {/* Bouton scroll to bottom */}
      {showScrollBottom && (
        <button
          onClick={() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' })}
          className="absolute lg:bottom-20 bottom-36 right-4 bg-emerald-500 hover:bg-emerald-400 text-white rounded-full p-2 shadow-lg transition-all z-10"
          aria-label="Défiler vers le bas"
        >
          <ChevronUp className="h-4 w-4 rotate-180" />
        </button>
      )}

      {/* Input */}
      <div className="lg:pb-0 pb-16">
        <MessageInput onSend={handleSend} disabled={sending} />
      </div>

      <FlowDeliverablesDrawer
        open={deliverablesOpen}
        onClose={() => setDeliverablesOpen(false)}
        conversationId={conversationId}
        flowSessionToken={flowSessionToken}
        flowMeta={flowMeta}
        flowTemplateId={flowTemplateId ?? agent?.flow_template_id}
      />
    </div>
  );
}
